/**
 * Search Service
 *
 * Frontend client for the universal search API.
 * All search consumers (omnibar, SearchResultsView, entity_find augmentation,
 * mail backend search) go through this service.
 */

import { API_BASE_URL } from '../api/config';
import fetchService from './fetchService';
import { createFileLogger } from '../utils/logger';

const log = createFileLogger('SEARCH_SERVICE');

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface UniversalSearchRequest {
  query: string;
  mode?: 'navigational' | 'research' | 'answer';
  entityTypes?: string[];
  filters?: Record<string, any>;
  includePassages?: boolean;
  context?: { spaceId?: string; activeEntityIds?: string[] };
  limit?: number;
}

export interface PassageHit {
  text: string;
  position: number;
  section_title?: string;
}

export interface UniversalSearchResult {
  entity_id: string;
  name: string;
  entity_type: string;
  icon?: string;
  score: number;
  match_source: string;
  passages?: PassageHit[];
  snippet?: string;
  updated_at?: string;
  llm_id?: string;
}

export interface UniversalSearchResponse {
  results: UniversalSearchResult[];
  total_candidates: number;
  elapsed_ms: number;
  mode: string;
  query: string;
}

export interface SearchHealthResponse {
  entity_search_index?: { count: number };
  content_chunks?: { count: number };
  entity_vectors?: { available: boolean; count?: number };
  chunk_vectors?: { available: boolean; count?: number };
  elapsed_ms?: number;
}

export interface SearchStatsResponse {
  entity_search_index: Record<string, number>;
  content_chunks: Record<string, number>;
  elapsed_ms: number;
}

// ---------------------------------------------------------------------------
// API calls
// ---------------------------------------------------------------------------

export async function searchUniversal(
  params: UniversalSearchRequest,
): Promise<UniversalSearchResponse> {
  const t0 = performance.now();
  try {
    const body = {
      query: params.query,
      mode: params.mode || 'navigational',
      entity_types: params.entityTypes || null,
      filters: params.filters || null,
      include_passages: params.includePassages || false,
      context: params.context || null,
      limit: params.limit || 20,
    };

    const response = await fetchService(`${API_BASE_URL}/search/universal`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      credentials: 'include',
    });

    const elapsed = Math.round(performance.now() - t0);
    log.info(
      `[searchUniversal] query="${params.query}" mode=${body.mode} ` +
      `results=${response?.results?.length ?? 0} candidates=${response?.total_candidates ?? 0} ` +
      `backend_ms=${response?.elapsed_ms ?? '?'} total_ms=${elapsed}`,
    );

    return response as UniversalSearchResponse;
  } catch (error) {
    const elapsed = Math.round(performance.now() - t0);
    log.error(`[searchUniversal] failed after ${elapsed}ms:`, error);
    return {
      results: [],
      total_candidates: 0,
      elapsed_ms: elapsed,
      mode: params.mode || 'navigational',
      query: params.query,
    };
  }
}

export async function getSearchHealth(): Promise<SearchHealthResponse> {
  try {
    return (await fetchService(`${API_BASE_URL}/search/health`, {
      credentials: 'include',
    })) as SearchHealthResponse;
  } catch (error) {
    log.error('[getSearchHealth] failed:', error);
    return {};
  }
}

export async function getSearchStats(): Promise<SearchStatsResponse> {
  try {
    return (await fetchService(`${API_BASE_URL}/search/stats`, {
      credentials: 'include',
    })) as SearchStatsResponse;
  } catch (error) {
    log.error('[getSearchStats] failed:', error);
    return { entity_search_index: {}, content_chunks: {}, elapsed_ms: 0 };
  }
}

export async function runSmokeTest(): Promise<{ passed: boolean; steps: any[] }> {
  try {
    return (await fetchService(`${API_BASE_URL}/search/smoke-test`, {
      method: 'POST',
      credentials: 'include',
    })) as { passed: boolean; steps: any[] };
  } catch (error) {
    log.error('[runSmokeTest] failed:', error);
    return { passed: false, steps: [{ name: 'error', passed: false, detail: String(error) }] };
  }
}

const searchService = {
  searchUniversal,
  getSearchHealth,
  getSearchStats,
  runSmokeTest,
};

export default searchService;
